<template>
    <div class="a-tabs a-tabs-v1" :class="className">
        <div
            ref="header"
            class="a-tabs-header"
            :class="{ noBottomSpacing, noTopSpacing }"
        >
            <div class="a-tabs-list-wrap">
                <ul
                    class="a-tabs-list"
                    role="tablist"
                    :aria-label="ariaLabel"
                >
                    <li
                        v-for="(tab, i) in tabsList"
                        :key="tabKey(tab, i)"
                        class="a-tabs-list-item"
                        :class="{ active: i === activeIndex }"
                        :style="{ '--data-index': tabsList.length - i }"
                        role="presentation"
                        @click="handleClick($event, tab, i)"
                    >
                        <button
                            :id="tabElementId(i)"
                            :ref="(el) => setTabRef(el, i)"
                            class="a-tabs-item"
                            :class="{ active: i === activeIndex }"
                            :aria-selected="i === activeIndex"
                            :tabindex="i === focusIndex ? 0 : -1"
                            role="tab"
                            type="button"
                            @keydown="handleKeydown($event, i)"
                        >
                            <span v-if="tab.labelIcon" class="a-tabs-icon-wrap">
                                <a-icon
                                    class="a-tabs-icon"
                                    :number="tab.labelIcon"
                                    :icon-alt="tab.labelIconAlt || tab.label"
                                />
                            </span>
                            <span class="a-tabs-label">{{ tab.label }} <a-glyph v-if="tab.glyph" v-bind="tab.glyph" /></span>
                            <span v-if="tab.translatedLabel">
                                {{ tab.translatedLabel }}
                            </span>
                            <a-label v-if="tab?.aLabel?.text" v-bind="tab.aLabel">
                                {{ tab.aLabel.text }}
                            </a-label>
                        </button>
                    </li>
                </ul>
                <div ref="underline" class="a-tabs-underline" />
            </div>
        </div>
        <div ref="content" class="a-tabs-panel">
            <slot />
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useSlots, watch } from 'vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import AIcon from '@core/components/icon/icon.vue';
import ALabel from '@core/components/label/label.vue';
import { useResizeObserver } from '@core/composables/useResizeObserver';
import { keyboardTargetIndex, sendTabMenuEvent, tabKey, useTabs } from '@core/composables/useTabs';

defineOptions({ name: 'ATabsV1' });

const props = defineProps({
    tabs: {
        type: Array,
        default: null,
    },
    noTopSpacing: {
        type: Boolean,
        default: false,
    },
    noBottomSpacing: {
        type: Boolean,
        default: false,
    },
    useSmallTabs: {
        type: Boolean,
        default: false,
    },
    withIcons: {
        type: Boolean,
        default: false,
    },
    activeTab: {
        type: Number,
        default: undefined,
    },
    controlled: {
        type: Boolean,
        default: false,
    },
    typeWidth: {
        type: String,
        default: undefined,
    },
    idPrefix: {
        type: String,
        default: undefined,
    },
    ariaLabel: {
        type: String,
        default: undefined,
    },
    storageKey: {
        type: String,
        default: undefined,
    },
    doNotSendGA: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['tab-click', 'tab-change']);

const source = useTabs({ props, slots: useSlots(), emit });
const { tabsList, tabsSignature, activeIndex, focusIndex, selectTab, content, tabRefs, setTabRef, tabElementId } = source;

const header = ref(null);
const underline = ref(null);

const className = computed(() => {
    const tabCount = tabsList.value.length;

    return {
        'is-full-width': props.typeWidth === 'full',
        'a-tabs-with-icons': props.withIcons,
        'a-tabs-layout-few': tabCount <= 4 && !props.useSmallTabs,
        'a-tabs-layout-many': tabCount >= 5 || props.useSmallTabs,
    };
});

function setUnderline() {
    if (!header.value || !underline.value) return;

    const activeTab = tabRefs.value[activeIndex.value];
    if (!activeTab) {
        underline.value.style.width = '0px';
        underline.value.style.left = '0px';
        return;
    }

    const tabRect = activeTab.getBoundingClientRect();
    const headerLeft = header.value.getBoundingClientRect().left;

    underline.value.style.width = `${tabRect.width}px`;
    underline.value.style.left = `${tabRect.left - headerLeft + header.value.scrollLeft}px`;
}

function revealActiveTab() {
    const activeTab = tabRefs.value[activeIndex.value];
    if (!activeTab || !header.value) return;

    const RIGHT_SHIFT = 48;
    const LEFT_SHIFT = 64;
    const EDGE = 32;

    const tabRect = activeTab.getBoundingClientRect();
    const headerRect = header.value.getBoundingClientRect();
    const tabLeft = Math.floor(tabRect.x);
    const headerLeft = Math.floor(headerRect.x);
    const scrollTo = (left) => header.value.scrollTo({ left, behavior: 'smooth' });

    if (tabLeft - EDGE < headerLeft) {
        scrollTo(header.value.scrollLeft + tabLeft - headerLeft - LEFT_SHIFT);
        return;
    }

    if (tabLeft + tabRect.width + EDGE > headerLeft + headerRect.width) {
        scrollTo(header.value.scrollLeft + tabLeft + tabRect.width - headerRect.width + RIGHT_SHIFT);
    }
}

function handleClick(event, tab, i) {
    if (!props.doNotSendGA) sendTabMenuEvent(event, tab, i);
    emit('tab-click', { index: i, item: tab, event });
    selectTab(i);
}

function handleKeydown(event, index) {
    const nextIndex = keyboardTargetIndex(event, index, tabsList.value.length);
    if (nextIndex === null) return;
    event.preventDefault();
    tabRefs.value[selectTab(nextIndex)]?.focus();
}

function stateChange() {
    revealActiveTab();
    setUnderline();
}

watch(activeIndex, stateChange);

watch(tabsSignature, () => nextTick(setUnderline));

useResizeObserver(header, setUnderline);

onMounted(() => {
    window.addEventListener('resize', setUnderline);
    nextTick(setUnderline);
});

onBeforeUnmount(() => window.removeEventListener('resize', setUnderline));
</script>

<style lang="pcss" scoped>
.a-tabs-panel {
    position: relative;
}

.a-tabs-header {
    overflow: scroll;
    padding-bottom: 12px;
    margin-top: 56px;
    margin-bottom: 28px;
    transition: transform 0.3s ease-out;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        height: 0;
        background: rgba(255, 255, 255, 0);
    }

    &.noTopSpacing {
        margin-top: 0;
    }

    &.noBottomSpacing {
        margin-bottom: 0;
    }
}

.a-tabs-list-wrap {
    position: relative;
    padding-bottom: 2px;

    &::before {
        content: '';
        position: absolute;
        inset-inline-start: 0;
        bottom: 0;
        z-index: 1;
        width: 100%;
        height: 2px;
        background-color: var(--av-brand-secondary-accent);
    }
}

.a-tabs-list {
    position: relative;
    display: flex;
    align-items: center;
    border-radius: 4px;
}

.a-tabs-list-item {
    transition: box-shadow 0.3s ease-in-out;

    &:not(:last-of-type) {
        margin-inline-end: 24px;

        @media (--viewport-mobile-wide) {
            margin-inline-end: 40px;
        }
    }
}

.a-tabs-underline {
    display: block;
    background-color: var(--av-nav-primary);
    position: absolute;
    bottom: 0;
    height: 2px;
    transition: left ease-out 0.3s, width ease-out 0.3s;
}

.a-tabs-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 0 0 8px;
    background-color: rgba(255, 255, 255, 0);
    border: none;
    cursor: pointer;
    position: relative;
    text-align: center;

    &:hover .a-tabs-label {
        color: var(--av-brand-secondary);
    }

    &:focus .a-tabs-label {
        background: var(--av-brand-lightest);
        color: var(--av-brand-primary);
    }

    &.active .a-tabs-label {
        color: var(--av-nav-primary) !important;
    }

    &.active:focus .a-tabs-label {
        background: none;
    }
}

.a-tabs-icon-wrap {
    margin-bottom: 24px;
    width: 40px;
    height: 40px;
}

.a-tabs-label {
    color: var(--av-brand-primary);
    width: 100%;
    white-space: nowrap;
}

.a-tabs-layout-many {
    .a-tabs-list-item:not(:last-of-type) {
        margin-inline-end: 16px;

        @media (--viewport-desktop) {
            margin-inline-end: 24px;
        }
    }

    .a-tabs-label {
        @mixin body;
        font-weight: 600;

        @media (--viewport-desktop) {
            @mixin lead;
        }
    }
}

.a-tabs-layout-few {
    .a-tabs-list-item:not(:last-of-type) {
        margin-inline-end: 24px;

        @media (--viewport-mobile-wide) {
            margin-inline-end: 40px;
        }
    }

    .a-tabs-label {
        @mixin lead;

        @media (--viewport-desktop) {
            @mixin title;
        }
    }
}

.a-tabs-with-icons {
    .a-tabs-list-item {
        cursor: pointer;

        &:not(:last-of-type) {
            margin-inline-end: 0;
        }
    }

    .a-tabs-item {
        padding: 0 8px 40px;

        @media (--viewport-desktop-large) {
            padding: 0 24px 40px;
        }
    }

    .a-tabs-label {
        font-size: 16px;
        font-weight: 600;
        line-height: 24px;
        color: var(--av-nav-primary);
        white-space: normal;
    }
}

.is-full-width .a-tabs-list-item {
    flex: 1;
}

:deep(.a-label) {
    white-space: nowrap;
}
</style>
